// CustomDropdown.jsx - Shared dropdown component that prevents browser overlap
import React, { useState, useRef, useEffect } from 'react';

const CustomDropdown = ({ value, onChange, options, className = "", placeholder = "Select..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const selectedOption = options.find(opt => (opt.value || opt) === value);
  const displayValue = selectedOption ? (selectedOption.label || selectedOption) : placeholder;

  // Check if content is scrollable when dropdown opens
  useEffect(() => {
    if (isOpen && listRef.current) {
      const element = listRef.current;
      const hasScroll = element.scrollHeight > element.clientHeight;
      setShowScrollIndicator(hasScroll);
      
      // Hide scroll indicator when user scrolls to bottom
      const handleScroll = () => {
        const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 5;
        setShowScrollIndicator(hasScroll && !isAtBottom);
      };
      
      element.addEventListener('scroll', handleScroll);
      return () => element.removeEventListener('scroll', handleScroll);
    }
  }, [isOpen, options.length]);

  return (
    <div ref={dropdownRef} className={`relative custom-dropdown ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full rounded-lg bg-slate-800/90 border border-white/10 text-white px-4 py-3 text-left focus:border-yellow-400/60 focus:outline-none flex items-center justify-between hover:bg-slate-700/80 transition-all duration-200 shadow-sm"
      >
        <span className="text-white font-medium">{displayValue}</span>
        <svg 
          className={`w-4 h-4 transition-transform duration-200 text-white ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <div 
          ref={listRef}
          className="absolute top-full left-0 right-0 mt-2 border border-white/10 rounded-lg shadow-2xl z-50 max-h-60 overflow-y-auto custom-dropdown backdrop-blur-sm" 
          style={{backgroundColor: 'rgba(30, 41, 59, 0.95)', backdropFilter: 'blur(8px)'}}
        >
          {options.map((opt, index) => {
            const optValue = opt.value || opt;
            const optLabel = opt.label || opt;
            const isSelected = optValue === value;
            return (
              <button
                key={index}
                type="button"
                onClick={() => {
                  onChange({ target: { value: optValue } });
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-3 text-left font-medium hover:bg-white/10 focus:bg-white/10 focus:outline-none transition-all duration-150 ${
                  index === 0 ? 'rounded-t-lg' : ''
                } ${
                  index === options.length - 1 ? 'rounded-b-lg' : ''
                } ${
                  isSelected ? 'bg-yellow-400/15 text-yellow-300' : ''
                }`}
                style={{color: isSelected ? '#fde047' : '#f8fafc', backgroundColor: 'transparent', border: 'none'}}
              >
                {optLabel}
              </button>
            );
          })}
          
          {/* Scroll Indicator - Fixed at bottom */}
          {showScrollIndicator && (
            <div className="sticky bottom-0 left-0 right-0 bg-gradient-to-t from-slate-700 via-slate-700/80 to-transparent pt-2 pb-2 pointer-events-none">
              <div className="flex justify-center">
                <div className="bg-yellow-400/95 text-slate-900 px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 shadow-lg animate-pulse">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                  Scroll for more players
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
